import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode,
} from 'react'
import { useSelector } from 'react-redux'
import { useQueryClient } from '@tanstack/react-query'
import type { GridColumnState } from '@/components/grid/types'
import { queryKeys } from '@/constant/common'
import { NotifyService } from '@/helpers'
import { useAuthContext } from '@/features/auth/context'
import { selectUser } from '@/store/selectors/UserSelector'
import useTableSavedViews from '@/features/config/hooks/savedViews/useTableSavedViews'
import useSaveTableSavedViews from '@/features/config/hooks/savedViews/useSaveTableSavedViews'
import {
  EMPTY_SAVED_VIEWS,
  SavedView,
  TableSavedViewsBlob,
} from '@/types/table-saved-views'

export type { SavedView }

interface SavedViewsContextValue {
  savedViews: SavedView[]
  activeViewName: string | null
  /**
   * False until the server has answered — and false for good if it never
   * does. Views stay readable from the local seed either way, but a write
   * that cannot reach the server would be silently lost on the next load,
   * so the controls that write are disabled rather than lying.
   */
  canPersist: boolean
  saveView: (name: string, columns: GridColumnState[]) => void
  deleteView: (name: string) => void
  applyView: (
    name: string,
    onApplyColumns: (columns: GridColumnState[]) => void,
  ) => void
  clearActiveView: () => void
}

const SavedViewsContext = createContext<SavedViewsContextValue | null>(null)

/**
 * Views saved before the grid carried order and width were a plain
 * `{ [field]: visible }` map. They are read back as a state array so an
 * existing view keeps working — the columns it hid stay hidden, and the
 * order and widths are whatever the grid is already showing.
 */
const parseStoredViews = (raw: string | null): SavedView[] => {
  if (!raw) return []

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return []
  }
  if (!Array.isArray(parsed)) return []

  return parsed.flatMap((view) => {
    if (!view || typeof view.name !== 'string') return []

    if (Array.isArray(view.columns)) return [view as SavedView]

    if (view.columns && typeof view.columns === 'object') {
      const columns = Object.entries(
        view.columns as Record<string, boolean>,
      ).map(([colId, visible]) => ({ colId, hide: visible === false }))
      return [{ name: view.name, columns }]
    }

    return []
  })
}

const readBlob = (viewsKey: string, activeKey: string): TableSavedViewsBlob => {
  try {
    return {
      items: parseStoredViews(localStorage.getItem(viewsKey)),
      activeViewName: localStorage.getItem(activeKey) || null,
    }
  } catch {
    return EMPTY_SAVED_VIEWS
  }
}

interface SavedViewsProviderProps {
  tableKey: string
  children: ReactNode
}

export const SavedViewsProvider = ({
  tableKey,
  children,
}: SavedViewsProviderProps) => {
  const { interfaceId } = useAuthContext()
  const user = useSelector(selectUser)
  const userId = user?.id
  const queryClient = useQueryClient()

  // The server holds one record per interface, so the local copy of it is
  // keyed the same way. Sharing one key across interfaces would show the
  // outgoing interface's views for a paint before the server corrected them.
  const seedViewsKey = `${tableKey}:${interfaceId ?? 'none'}-saved-views`
  const seedActiveKey = `${tableKey}:${interfaceId ?? 'none'}-active-view`
  // What every build before the server-backed one wrote, for any interface.
  const legacyViewsKey = `${tableKey}-saved-views`
  const legacyActiveKey = `${tableKey}-active-view`

  const [blob, setBlob] = useState<TableSavedViewsBlob>(() => {
    const seed = readBlob(seedViewsKey, seedActiveKey)
    if (seed.items.length > 0) return seed
    return readBlob(legacyViewsKey, legacyActiveKey)
  })
  const [recordId, setRecordId] = useState<string | undefined>()

  const blobRef = useRef(blob)
  blobRef.current = blob

  // Read at write time rather than closed over: two writes issued before the
  // first create has answered would both see `undefined` and each create their
  // own record.
  const recordIdRef = useRef<string | undefined>(recordId)
  // Writes run one after another for the same reason — the id only exists
  // once the create has come back, so the next write has to wait for it.
  const writeChainRef = useRef<Promise<void>>(Promise.resolve())

  const query = useTableSavedViews(interfaceId, userId, tableKey)
  // Only the mutate fn is taken: the mutation object itself is rebuilt on
  // every state change, and `write` is an effect dependency below.
  const { mutateAsync: saveViews } = useSaveTableSavedViews()

  const canPersist = query.isSuccess && Boolean(interfaceId) && Boolean(userId)

  const queryKey = useMemo(
    () => [queryKeys.tableSavedViews, interfaceId, userId, tableKey],
    [interfaceId, userId, tableKey],
  )

  const write = useCallback(
    (next: TableSavedViewsBlob, previous: TableSavedViewsBlob) => {
      if (!interfaceId || !userId) return

      writeChainRef.current = writeChainRef.current
        .then(async () => {
          const record = await saveViews({
            interfaceId,
            userId,
            tableKey,
            recordId: recordIdRef.current,
            views: next,
          })
          recordIdRef.current = record.id
          setRecordId(record.id)
          // Seeded rather than invalidated: a refetch racing this write would
          // put the server's older snapshot back over what was just saved.
          queryClient.setQueryData(queryKey, record)
        })
        .catch(() => {
          setBlob(previous)
          NotifyService.error('Could not save your view. Please try again.')
        })
    },
    [interfaceId, userId, tableKey, saveViews, queryClient, queryKey],
  )

  /**
   * Optimistic: the grid reflects the change at once and the write follows,
   * with the state it replaced held so a rejected write can put it back.
   */
  const persist = useCallback(
    (next: TableSavedViewsBlob) => {
      const previous = blobRef.current
      setBlob(next)
      write(next, previous)
    },
    [write],
  )

  // The server is authoritative: whatever it returns replaces the seed
  // wholesale. Writes are blocked until this has run, so it cannot land on
  // top of a change the user has already made.
  //
  // It also carries the one-time migration off the pre-server storage. Those
  // keys are per grid but not per interface, so they are claimed at most once
  // per session — otherwise switching to an interface that happens to have no
  // record would upload the previous interface's views into it.
  const migratedRef = useRef(false)
  useEffect(() => {
    if (!query.isSuccess) return

    const dropLegacy = () => {
      try {
        localStorage.removeItem(legacyViewsKey)
        localStorage.removeItem(legacyActiveKey)
      } catch {
        // Storage disabled; the ref below still stops a second attempt.
      }
    }

    if (query.data) {
      recordIdRef.current = query.data.id
      setRecordId(query.data.id)
      setBlob(query.data.views ?? EMPTY_SAVED_VIEWS)
      // Server views exist for this grid, so anything held locally from
      // before the migration has been superseded.
      if (!migratedRef.current) {
        migratedRef.current = true
        dropLegacy()
      }
      return
    }

    recordIdRef.current = undefined
    setRecordId(undefined)

    if (migratedRef.current) return

    const local = readBlob(legacyViewsKey, legacyActiveKey)
    if (local.items.length === 0) {
      migratedRef.current = true
      return
    }

    // Claim the local copy only once it can actually be written up. Deleting
    // the keys first would lose the views outright if the write never went.
    if (!canPersist) return

    migratedRef.current = true
    setBlob(local)
    write(local, EMPTY_SAVED_VIEWS)
    dropLegacy()
  }, [
    query.isSuccess,
    query.data,
    legacyViewsKey,
    legacyActiveKey,
    canPersist,
    write,
  ])

  // An interface switch changes which record is being read without
  // remounting, so the blob left over from the previous one is dropped rather
  // than shown. This effect is declared before the one that writes the seed,
  // so the re-read lands first and no blob is ever written under a key it did
  // not come from.
  const seededKeyRef = useRef(seedViewsKey)
  useEffect(() => {
    if (seededKeyRef.current === seedViewsKey) return
    seededKeyRef.current = seedViewsKey
    recordIdRef.current = undefined
    setRecordId(undefined)
    setBlob(readBlob(seedViewsKey, seedActiveKey))
  }, [seedViewsKey, seedActiveKey])

  // Keep the local seed in step so the next mount paints before the request.
  useEffect(() => {
    if (seededKeyRef.current !== seedViewsKey) return
    try {
      localStorage.setItem(seedViewsKey, JSON.stringify(blob.items))
      if (blob.activeViewName)
        localStorage.setItem(seedActiveKey, blob.activeViewName)
      else localStorage.removeItem(seedActiveKey)
    } catch {
      // Storage full or unavailable; the server copy is what matters.
    }
  }, [blob, seedViewsKey, seedActiveKey])

  const saveView = useCallback(
    (name: string, columns: GridColumnState[]) => {
      const current = blobRef.current
      persist({
        activeViewName: name,
        items: [
          ...current.items.filter((view) => view.name !== name),
          { name, columns },
        ],
      })
    },
    [persist],
  )

  const deleteView = useCallback(
    (name: string) => {
      const current = blobRef.current
      persist({
        activeViewName:
          current.activeViewName === name ? null : current.activeViewName,
        items: current.items.filter((view) => view.name !== name),
      })
    },
    [persist],
  )

  const applyView = useCallback(
    (name: string, onApplyColumns: (columns: GridColumnState[]) => void) => {
      const current = blobRef.current
      const view = current.items.find((v) => v.name === name)
      if (!view) return
      onApplyColumns(view.columns)
      persist({ ...current, activeViewName: name })
    },
    [persist],
  )

  // Fires on every column toggle, Show All, Hide All and preset, so it only
  // writes when there was an active view to clear.
  const clearActiveView = useCallback(() => {
    const current = blobRef.current
    if (current.activeViewName === null) return
    persist({ ...current, activeViewName: null })
  }, [persist])

  const value = useMemo(
    () => ({
      savedViews: blob.items,
      activeViewName: blob.activeViewName,
      canPersist,
      saveView,
      deleteView,
      applyView,
      clearActiveView,
    }),
    [blob, canPersist, saveView, deleteView, applyView, clearActiveView],
  )

  return (
    <SavedViewsContext.Provider value={value}>
      {children}
    </SavedViewsContext.Provider>
  )
}

export const useSavedViewsContext = () => {
  const ctx = useContext(SavedViewsContext)
  if (!ctx)
    throw new Error(
      'useSavedViewsContext must be used within a SavedViewsProvider',
    )
  return ctx
}
